import React, { useMemo } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import ToastService from 'services/toast.service'

import { ExporterProps } from 'components/deckPage/modals/Export/types'

import PhatButton from 'components/formElements/PhatButton'
import PhatDropdown from 'components/formElements/PhatDropdown'
import CheckboxDropdown from 'components/formElements/CheckboxDropdown'
import Checkbox from 'components/formElements/Checkbox'

import { download } from 'utils/downloadHelper'
import { buildTextFile, generateExampleLine, SectionHeader } from './helper'

import genericStyles from 'components/deckPage/modals/Export/generic.module.scss'

const LOCAL_STORAGE_KEY = 'archidekt_text_export_options'

export const fileGenerator = buildTextFile

type LocalStorageOptions = {
  stringDisplayFlags: string[]
  frontNameOnly: boolean
  sectionHeader: SectionHeader
}

const defaultOptions: LocalStorageOptions = {
  stringDisplayFlags: [
    'includeQuantityX',
    'includeSetCode',
    'includeCollectorNumber',
    'includeFoil',
    'includeCategories',
    'includeColorTags',
  ],
  frontNameOnly: false,
  sectionHeader: 'none',
}

const TextFile = ({ selectedCards, fileName = 'archidekt_export', categoryMap, onSubmit }: ExporterProps) => {
  const [options, setOptions] = useLocalStorage<LocalStorageOptions>(LOCAL_STORAGE_KEY, defaultOptions)

  const { includeQuantityX, includeSetCode, includeCollectorNumber, includeFoil, includeCategories, includeColorTags } =
    useMemo(() => {
      return {
        includeQuantityX: options.stringDisplayFlags.includes('includeQuantityX'),

        includeSetCode: options.stringDisplayFlags.includes('includeSetCode'),
        includeCollectorNumber: options.stringDisplayFlags.includes('includeCollectorNumber'),
        includeFoil: options.stringDisplayFlags.includes('includeFoil'),
        includeCategories: options.stringDisplayFlags.includes('includeCategories'),
        includeColorTags: options.stringDisplayFlags.includes('includeColorTags'),
      }
    }, [options.stringDisplayFlags])

  // prettier-ignore
  const { exampleWithNoHeaders, exampleWithHeaders } = generateExampleLine(includeQuantityX, options.frontNameOnly, includeSetCode, includeCollectorNumber, includeFoil, includeCategories, includeColorTags, options.sectionHeader) // Example line for the dropdown

  // prettier-ignore
  const fileBody = buildTextFile(selectedCards, categoryMap, includeQuantityX, options.frontNameOnly, includeSetCode, includeCollectorNumber, includeFoil, includeCategories, includeColorTags, options.sectionHeader) // Actual file body

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(fileBody)

      ToastService.create('Copied to clipboard', 'Deck Export', 'success')
    } catch {
      ToastService.create('Unable to copy to clipboard', 'Deck Export')
    }
  }

  const handleDownload = () => {
    try {
      const cleanedFileName = `${fileName.toLowerCase().replace(' ', '_')}.txt`

      download(cleanedFileName, fileBody)
    } catch {
      ToastService.create('Unable to download file', 'Deck export', 'error')
    }
  }

  return (
    <div>
      <CheckboxDropdown
        value={options.stringDisplayFlags}
        includeSelectAllNoneOptions
        onChange={updatedFlags => setOptions({ ...options, stringDisplayFlags: updatedFlags })}
        header="Export options"
        selectedOptionsDisplayOverride={exampleWithNoHeaders}
        options={[
          {
            label: 'Include x in quantity',
            id: 'includeQuantityX',
          },
          {
            label: 'Include set code',
            id: 'includeSetCode',
          },
          {
            label: 'Include collector number',
            id: 'includeCollectorNumber',
          },
          {
            label: 'Include foil indicator',
            id: 'includeFoil',
          },
          {
            label: 'Include categories',
            id: 'includeCategories',
          },
          {
            label: 'Include color tag data',
            id: 'includeColorTags',
          },
        ]}
      />

      <PhatDropdown
        inlineHeader
        header="Section header"
        value={options.sectionHeader}
        options={[
          { label: 'No section header', id: 'none', onClick: () => setOptions({ ...options, sectionHeader: 'none' }) },
          {
            label: 'Primary category',
            id: 'primaryCategory',
            onClick: () => setOptions({ ...options, sectionHeader: 'primaryCategory' }),
          },
          { label: 'Card type', id: 'cardType', onClick: () => setOptions({ ...options, sectionHeader: 'cardType' }) },
          {
            label: 'Generic (eg: Commander, Mainboard, Sideboard)',
            id: 'generic',
            onClick: () => setOptions({ ...options, sectionHeader: 'generic' }),
          },
        ]}
      />

      <Checkbox
        checked={options.frontNameOnly}
        onChange={() => setOptions({ ...options, frontNameOnly: !options.frontNameOnly })}
        label="Use front name only for MDFC cards"
      />

      <h4>Export example</h4>
      <div className={genericStyles.example}>{exampleWithHeaders}</div>

      {/* Commenting out warning because I have ZERO trust that users are gonna read this anyway. I'm torn whether or not this should be here */}
      {/* !isEqual(options.stringDisplayFlags, defaultOptions.stringDisplayFlags) && (
        <div className={genericStyles.warning}>
          WARNING: The export options you've chosen are lacking details to recreate deck in full. Exporting with these
          details will make it impossible to recreate the deck 1:1 with all printings, categories, etc.
          <br />
          <br />
          This is fine for a generic export, but if you want to re-import the deck identical to what it is now, you
          should probably include all details.
        </div>
      )} */}

      <div className={genericStyles.controls}>
        {!onSubmit && (
          <PhatButton icon="copy outline" color="blue" onClick={handleCopy}>
            Copy
          </PhatButton>
        )}
        <PhatButton
          icon="download"
          color="green"
          onClick={() =>
            !onSubmit
              ? handleDownload()
              : onSubmit([
                  includeQuantityX,
                  options.frontNameOnly,
                  includeSetCode,
                  includeCollectorNumber,
                  includeFoil,
                  includeCategories,
                  includeColorTags,
                  options.sectionHeader,
                ])
          }>
          Download
        </PhatButton>
      </div>
    </div>
  )
}

export default TextFile
